import { useState, useEffect, useRef } from 'react';

import { SUPPORTED_LANGUAGES } from "./supportedLanguagesModel.js";

import Button from "@cloudscape-design/components/button";
import Select from "@cloudscape-design/components/select";
import Textarea from "@cloudscape-design/components/textarea";
import { translateMessage } from "./service/translateService.js";

function Interface() {
  const [selectedAgentLang, setSelectedAgentLang] = useState(SUPPORTED_LANGUAGES.find((lang) => lang.value === 'fr'));
  const [selectedClientLang, setSelectedClientLang] = useState(SUPPORTED_LANGUAGES.find((lang) => lang.value === 'en')); // auto-détecté
  const [inputMessage, setInputMessage] = useState("");
  const [translatedMessage, setTranslatedMessage] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitchClientLang, setIsSwitchClientLang] = useState(false);

  const [chatSession, setChatSession] = useState(null);
  
  const ccpContainerRef = useRef(null);
  const isListenerAdded = useRef(false);
  const authToken = useRef(null);
  const selectedAgentLangRef = useRef(selectedAgentLang);
  const selectedClientLangRef = useRef(selectedClientLang);
  
  const init = () => {
    const ccpUrl = "https://econocom-dev.my.connect.aws/connect/ccp-v2";
    const connectRegion = "eu-central-1";

    window.connect.core.initCCP(ccpContainerRef.current, {
      ccpUrl: ccpUrl,
      region: connectRegion,
      loginPopup: false,
      softphone: {
        allowFramedSoftphone: true,
      }
    });
  };

  const handleAgentToken = () => {
    connect.agent((agent) => {
      const agentName = agent.getName();
      const timestamp = Math.floor(Date.now() / 1000);

      authToken.current = `ConnectAgent:${agentName}:${timestamp}`;
      console.log('✅ Agent authentifié');
    });
  }
  
  useEffect(() => {    
    init();
    handleAgentToken();
    handleContactSubscribe();
  }, []);

  useEffect(() => {
    selectedAgentLangRef.current = selectedAgentLang;
  }, [selectedAgentLang]);
  
  useEffect(() => {
    selectedClientLangRef.current = selectedClientLang;
  }, [selectedClientLang]);

  const handleContactSubscribe = () => {
    connect.contact(async contact => {
      console.log("Nouveau contact entrant", contact);
      const agentConnection = contact.getConnections().find(conn => conn.getType() === connect.ConnectionType.AGENT);
      const agentChatSession = await agentConnection.getMediaController();
      setChatSession(agentChatSession);
      
      if (!isListenerAdded.current) {
        agentChatSession.onMessage(async (event) => {
          const message = event.data?.Content;
          const participant = event.data?.ParticipantRole;
          
          if (message && participant === 'CUSTOMER') {
            handleOnClientMessage(message);
          }
        });

        isListenerAdded.current = true;
      }

      contact?.onEnded(contact => {
        console.log("Contact ended");
        setTranscript([]);
        isListenerAdded.current = false;
      })
    })
  }

  const handleOnClientMessage = async (message) => {
    const agentLang = selectedAgentLangRef.current;
    // const clientLang = selectedClientLangRef.current;
    const clientLang = "auto";
    const translatedMsg = await callTranslateAPI(message, "translate", clientLang, agentLang.value)
    
    const foundLang = SUPPORTED_LANGUAGES.find((lang) => lang.value === translatedMsg.source_language);
    setSelectedClientLang(foundLang);
    setIsSwitchClientLang(false);
    setTranscript((prev) => [...prev, { role: "Utilisateur", message: translatedMsg.translated_text }]);
  }

  const callTranslateAPI = async (message, typeOfClientCall, source_lang = "auto", target_lang = "en") => {
    if (!authToken.current) {
      alert("Vous n'êtes pas connecté à Connect");
      console.error("Translate API call unauthorized");
      
      return;
    }

    try {
      setIsLoading(true);
      const response = await translateMessage(authToken.current, message, typeOfClientCall, source_lang, target_lang);
      const data = await response.json();
      const responseObj = JSON.parse(data.body)
      
      return responseObj;
    } catch(e) {
      console.log('Erreur lors de l\'appel api Translate', e);
    } finally {
      setIsLoading(false);
    }
  }

  const handleTranslate = async (inputMessage, typeOfClientCall="translate") => {
    const translateResp = await callTranslateAPI(inputMessage, typeOfClientCall, selectedAgentLang?.value, selectedClientLang?.value)

    const translatedMsg = translateResp?.translated_text;
    const sourceLang = translateResp?.source_language;
    setTranslatedMessage(translatedMsg);
  };

  const handleSend = async () => {
    setTranscript((prev) => [...prev, { role: "Agent", message: inputMessage || "[Aucune traduction] : " + translatedMessage }]);
    setInputMessage("");
    setTranslatedMessage("");
    await chatSession.sendMessage({ contentType: "text/plain", message: translatedMessage });
  };
  
  const handleUnableDisableBtn = () => {
    setIsSwitchClientLang(!isSwitchClientLang);
  }
  
  return (
    <>
      <header>
        <h1 className='text-[#077398] text-[1.5rem]! border-b-1 border-black/20 pb-4'>Chat Multilingue</h1>
      </header>

      <div className="flex flex-col md:flex-row h-full gap-6">
        {/* Section CCP */}
        <div id="ccp" className='order-first shrink-0 border-3 border-[#3c454f]' ref={ccpContainerRef}></div>

        {/* Section Traduction */}
        <div className="flex-1 space-y-4 order-last">
          <h2 className="text-xl font-semibold">Traduction temps réel Chat</h2>

          <div className="flex gap-4">
            <div className="w-50">
              <label className="block text-sm mb-2">Langue de l’agent</label>

              <Select
                selectedOption={selectedAgentLang}
                onChange={({ detail }) =>
                  setSelectedAgentLang(detail.selectedOption)
                }
                options={SUPPORTED_LANGUAGES}
              />
            </div>

            <div className="w-50">
              <label className="block text-sm mb-2">Langue du client { isSwitchClientLang ? '' : '(auto)'}</label>
                
              <div className="flex justify-between">
                <Select
                  disabled={!isSwitchClientLang}
                  selectedOption={selectedClientLang}
                  onChange={({ detail }) =>
                    setSelectedClientLang(detail.selectedOption)
                  }
                  options={SUPPORTED_LANGUAGES}
                  className="min-w-full"
                />

                {!isSwitchClientLang && <Button iconName="check" variant="icon" onClick={handleUnableDisableBtn}></Button>}
                
                {isSwitchClientLang && <Button iconName="close" variant="icon" onClick={handleUnableDisableBtn}></Button>}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2">Votre message</label>
            <Textarea
              onChange={({ detail }) => setInputMessage(detail.value)}
              value={inputMessage}
              placeholder="Votre message"
              rows={6}
            />
          </div>

          <Button variant="primary" onClick={() => handleTranslate(inputMessage)} disabled={!inputMessage} loading={isLoading}>Traduire (Translate)</Button>
          <Button variant="primary" className="ml-5" onClick={() => handleTranslate(inputMessage, 'bedrock')} disabled={!inputMessage} loading={isLoading}>Traduire (Bedrock)</Button>

          <div>
            <label className="block text-sm mb-2">Traduction</label>
            <Textarea
              onChange={({ detail }) => setTranslatedMessage(detail.value)}
              value={translatedMessage}
              placeholder="Message traduit"
              rows={6}
            />
          </div>

          <Button variant="primary" onClick={handleSend} loading={isLoading} disabled={!translatedMessage || !isListenerAdded.current}>Envoyer</Button>
        </div>

        {/* Séparateur vertical */}
        {/* <div className="w-px bg-black/20 hidden md:block ml-5 mr-5 order-2 shrink-0"></div> */}

        {/* Section Transcript */}
        <div className="flex-1 order-3 max-w-1/4 min-w-48 ml-4">
          <h2 className="text-xl font-semibold mb-4">Transcript traduit</h2>
          <div className="space-y-2 whitespace-pre-line">
            {transcript.map((entry, index) => (
              <div key={index} className={entry.role === 'Utilisateur' ? 'client-transcript' : ''}>
                <strong>{entry.role} :</strong> {entry.message}
              </div>
            ))}
          </div>
        </div>
        
        <div className="w-px bg-black/20 hidden md:block ml-5 mr-5 order-4 shrink-0"></div>
      </div>
    </>
  )
}

export default Interface;